import * as React from 'react';
import * as moment from 'moment';
import { DateCalendar as MUICalendar } from '@mui/x-date-pickers/DateCalendar';
import { DateFormat } from '../../../../common/utils/dateUtils';
import CalendarDayRenderer from './calendarDayRenderer';
import Grid from '@mui/material/Grid';
import { DateView } from '@mui/x-date-pickers';
import { useMemo } from 'react';

interface CalendarProps {
	display: 'monthly' | 'yearly';
	displayedDate: string;  // for monthly view
	displayedYear: number;  // for yearly view
	onYearChange: (year: moment.Moment) => void;
	onChange: (date: moment.Moment) => void;
	isDisabled: (date: moment.Moment) => boolean;
	getDayColor: (date: moment.Moment) => string | undefined;
	getDayIcon: (date: moment.Moment) => React.ReactNode;
	shouldDisableYear: (year: moment.Moment) => boolean;
}

interface DateCalendarProps {
	monthDate: moment.Moment;
	displayedDate: string;
	className: string;
	views?: DateView[];
	onYearChange: (year: moment.Moment) => void;
	onChange: (date: moment.Moment) => void;
	isDisabled: (date: moment.Moment) => boolean;
	getDayColor: (date: moment.Moment) => string;
	getDayIcon: (date: moment.Moment) => React.ReactNode;
	shouldDisableYear: (year: moment.Moment) => boolean;
}

const DateCalendar: React.FC<DateCalendarProps> = (props) => {
	const { monthDate, displayedDate, className, views, onYearChange, onChange, isDisabled, getDayColor, getDayIcon, shouldDisableYear } = props;
	const defaultViews: Array<DateView> = [ 'year', 'month', 'day' ];
	// Determine if the displayedDate is within the current month being rendered
	const isSameMonth = monthDate.isSame(moment(displayedDate, DateFormat.LEGACY_MOMENT_SHORT_ISO), 'month');
	// Set value to displayedDate if it's the same month, otherwise null
	const calendarValue = isSameMonth ? moment(displayedDate, DateFormat.LEGACY_MOMENT_SHORT_ISO) : null;

	return (
		<MUICalendar
			key={className + '-' + monthDate}
			className={className}
			value={calendarValue}
			defaultCalendarMonth={monthDate} // Set the month to be displayed
			onYearChange={onYearChange}
			onChange={onChange}
			slots={ {day: CalendarDayRenderer} }
			slotProps={ {
				day: {
					isDisabled,
					getDayColor,
					getDayIcon
				} as any
			} }
			shouldDisableYear={ shouldDisableYear }
			views={ views || defaultViews }
		/>
	);
};

const OptimizedDateCalendar = React.memo(DateCalendar);

const Calendar = ({
	                  display,
	                  displayedDate,
	                  displayedYear,
	                  onYearChange,
	                  onChange,
	                  isDisabled,
	                  getDayColor,
	                  getDayIcon,
	                  shouldDisableYear
                  }: CalendarProps) => {

	if (display === 'monthly') {
		return <OptimizedDateCalendar monthDate={moment(displayedDate, DateFormat.LEGACY_MOMENT_SHORT_ISO)}
		                     displayedDate={displayedDate}
		                     className={'monthlyView'}
		                     onYearChange={onYearChange}
		                     onChange={onChange}
			                   isDisabled={isDisabled}
			                   getDayColor={getDayColor}
			                   getDayIcon={getDayIcon}
			                   shouldDisableYear={shouldDisableYear}
		/>;
	} else if (display === 'yearly') {
		const months = useMemo(() => Array.from({ length: 12 }, (_, i) =>
			moment().year(displayedYear).month(i).startOf('month')), [displayedYear]);
		return (
			<Grid container spacing={2}>
				{months.map(monthMoment => (
					<Grid item xs={12} sm={6} md={6} lg={3} key={monthMoment.format('MM')}>
						<OptimizedDateCalendar monthDate={monthMoment}
						              displayedDate={displayedDate}
						              className={'yearlyView'}
						              views={['day']}
						              onYearChange={onYearChange}
						              onChange={onChange}
						              isDisabled={isDisabled}
						              getDayColor={getDayColor}
						              getDayIcon={getDayIcon}
						              shouldDisableYear={shouldDisableYear}
					/>
					</Grid>
				))}
			</Grid>
		);
	} else {
		return <div>Invalid display mode</div>;
	}
};

export default React.memo(Calendar);
